import React, { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import s from '../styles.module.scss'

import FooterLogin from '../footer-login/footer-login'
import { ForgotPasswordInterface } from '../login.types'
import { InputText } from '../../../../components/molecules/input-text'
import { ForgotPasswordDto } from '../../../../lib/graphql'
import { formatGqlError } from '../../../../utils/format-error'
import regex from '../../../../utils/regex'

export interface InputsForgot {
  email: string
}

interface ForgotPassword extends ForgotPasswordInterface {
  setShowForgotPassword: React.Dispatch<React.SetStateAction<boolean>>
}

const ForgotPassword = ({
  onForgotPassword,
  isLoading,
  setShowForgotPassword,
}: {
  onForgotPassword: ({ email }: InputsForgot) => void
  isLoading: boolean
  setShowForgotPassword: (arg: boolean) => void
}) => {
  const [showForgotPasswordConfirm, setShowForgotPasswordConfirm] =
    useState<boolean>(false)
  const { control, handleSubmit, getValues, setError } =
    useForm<ForgotPasswordDto>({
      defaultValues: {
        email: '',
      },
    })

  const onSubmit: SubmitHandler<ForgotPasswordDto> = async () => {
    try {
      await onForgotPassword({ email: getValues('email') })
      setShowForgotPasswordConfirm(true)
    } catch (e) {
      const gqlError = formatGqlError(e)
      if (gqlError?.message === 'EMAIL_NOT_FOUND') {
        setError('email', {
          type: 'string',
          message: 'Adresse e-mail non reconnue',
        })
      } else if (gqlError?.message === 'OWNER_NOT_ACTIVE') {
        setError('email', {
          type: 'string',
          message:
            "Votre espace hôte n'est pas actif. Veuillez contacter le support.",
        })
      } else {
        setError('email', {
          type: 'string',
          message:
            'Une erreur est survenue. Veuillez réessayer ultérieurement.',
        })
      }
    }
  }

  return (
    <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
      <h2 className={s.title}>
        {showForgotPasswordConfirm
          ? 'Lien de réinitialisation du mot de passe envoyé'
          : 'Réinitialisation du mot de passe'}
      </h2>
      <div className={s.description}>
        {showForgotPasswordConfirm
          ? 'Vous retrouverez le lien dans un e-mail en provenance de Naboo sur votre boîte de réception'
          : 'Entrez votre e-mail ci-dessous, nous vous enverrons un lien pour réinitialiser votre mot de passe.'}
      </div>
      <InputText
        control={control}
        name='email'
        label={
          showForgotPasswordConfirm
            ? 'Adresse e-mail de destination'
            : 'Entrez votre adresse e-mail'
        }
        rules={{
          required: 'Une adresse e-mail est requise.',
          pattern: {
            value: regex.email,
            message: "Respecter le bon format d'e-mail",
          },
        }}
        inputProps={{
          type: 'text',
          'aria-label': 'entrez votre e-mail',
          'aria-required': 'true',
        }}
        placeholder='Entrez votre e-mail'
        disabled={showForgotPasswordConfirm}
        dataTestId='forgot-password-email-input'
      />

      <FooterLogin
        footerElement={
          <div className={s.link}>
            <span
              className={s.underline}
              onClick={() => setShowForgotPassword(false)}
            >
              {showForgotPasswordConfirm
                ? `Retour à l'écran de connexion`
                : 'Précédent'}
            </span>
          </div>
        }
        submit={
          !showForgotPasswordConfirm
            ? {
                isLoading,
                onSubmit: handleSubmit(onSubmit),
                label: 'Envoyer',
                isValid: true,
              }
            : undefined
        }
      />
    </form>
  )
}

export default ForgotPassword
